import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { useGetInventory } from "../../utils/react-query/queryInventory/queries";
import type {
  CreateReservationPayload,
  EditReservationPayload,
  ReservationDetail,
} from "./types";
import styles from "./ReservationModal.module.scss";

interface ReservationModalProps {
  open: boolean;
  /** If provided, the modal is in edit mode */
  reservation?: ReservationDetail | null;
  onClose: () => void;
  onCreate: (payload: CreateReservationPayload) => Promise<void>;
  onEdit: (id: number, payload: EditReservationPayload) => Promise<void>;
}

interface ItemRow {
  product_id: number;
  storage_address_id: number;
  quantity: number;
}

const emptyItem: ItemRow = { product_id: 0, storage_address_id: 0, quantity: 0 };

export function ReservationModal({
  open,
  reservation,
  onClose,
  onCreate,
  onEdit,
}: ReservationModalProps) {
  const isEditMode = !!reservation;
  const { data: inventory, isLoading: isLoadingInventory } = useGetInventory();

  const [notes, setNotes] = useState(reservation?.notes ?? "");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [quoteFieldError, setQuoteFieldError] = useState<string | null>(null);
  const [items, setItems] = useState<ItemRow[]>(
    reservation?.items?.map((i) => ({
      product_id: i.product_id,
      storage_address_id: i.storage_address_id,
      quantity: i.quantity,
    })) ?? [{ ...emptyItem }]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when reservation changes
  const handleEntering = () => {
    setNotes(reservation?.notes ?? "");
    setQuoteNumber("");
    setQuoteFieldError(null);
    setItems(
      reservation?.items?.map((i) => ({
        product_id: i.product_id,
        storage_address_id: i.storage_address_id,
        quantity: i.quantity,
      })) ?? [{ ...emptyItem }]
    );
  };

  // Validation
  const isItemsValid =
    items.length > 0 &&
    items.every(
      (item) =>
        item.product_id > 0 &&
        item.storage_address_id > 0 &&
        Number.isInteger(item.quantity) &&
        item.quantity > 0
    );
  const isQuoteValid =
    isEditMode ||
    (quoteNumber.trim().length > 0 && quoteNumber.trim().length <= 64);
  const canConfirm = isItemsValid && isQuoteValid && !isLoadingInventory;

  const handleItemChange = (
    index: number,
    field: keyof ItemRow,
    value: number
  ) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    setQuoteFieldError(null);
    try {
      if (isEditMode && reservation) {
        // Edit mode: send items as add/update/remove
        const payload: EditReservationPayload = {
          notes: notes.trim() || undefined,
          items: {
            add: items
              .filter((item) => !reservation.items?.some((ri) => ri.product_id === item.product_id && ri.storage_address_id === item.storage_address_id))
              .map((item) => ({
                product_id: item.product_id,
                storage_address_id: item.storage_address_id,
                quantity: item.quantity,
              })),
            update: items
              .filter((item) => reservation.items?.some((ri) => ri.product_id === item.product_id && ri.storage_address_id === item.storage_address_id))
              .map((item) => {
                const existing = reservation.items!.find(
                  (ri) =>
                    ri.product_id === item.product_id &&
                    ri.storage_address_id === item.storage_address_id
                )!;
                return { id: existing.id, quantity: item.quantity };
              }),
          },
        };
        await onEdit(reservation.id, payload);
      } else {
        // Create mode
        const payload: CreateReservationPayload = {
          quote_number: quoteNumber.trim(),
          notes: notes.trim() || undefined,
          items: items.map((item) => ({
            product_id: item.product_id,
            storage_address_id: item.storage_address_id,
            quantity: item.quantity,
          })),
        };
        await onCreate(payload);
      }
    } catch (err: any) {
      // Pluck backend field error for quote_number (inline display)
      // Non-field errors: parent (handleCreate) already shows generic errorToast
      const fieldErrors = err?.response?.data?.errors?.quote_number;
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        setQuoteFieldError(fieldErrors[0]);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEntering: handleEntering }}
    >
      <DialogTitle>
        {isEditMode ? "Editar Reserva" : "Nueva Reserva"}
      </DialogTitle>

      <DialogContent>
        {isEditMode ? (
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            {reservation?.quote_number || "Sin cotización"}
          </Typography>
        ) : (
          <TextField
            label="Cotización"
            variant="outlined"
            fullWidth
            margin="normal"
            value={quoteNumber}
            onChange={(e) => {
              setQuoteNumber(e.target.value);
              setQuoteFieldError(null);
            }}
            placeholder="COT-YYYYMMDD-HHMMSS"
            inputProps={{ maxLength: 64, "aria-label": "cotizacion" }}
            error={!!quoteFieldError}
            helperText={quoteFieldError}
          />
        )}

        <TextField
          label="Notas"
          variant="outlined"
          fullWidth
          multiline
            minRows={2}
          margin="normal"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          inputProps={{ maxLength: 500, "aria-label": "notas" }}
        />

        <Typography variant="subtitle2" className={styles.subtitle}>
          Items
        </Typography>

        {items.map((item, index) => (
          <div
            key={index}
            className={styles.itemRow}
          >
            <TextField
              select
              label="Producto"
              variant="outlined"
              size="small"
              value={item.product_id || ""}
              onChange={(e) =>
                handleItemChange(index, "product_id", parseInt(e.target.value, 10) || 0)
              }
              inputProps={{ "aria-label": `product-id-${index}` }}
              className={styles.itemField}
              disabled={isLoadingInventory}
            >
              <MenuItem value={0}>Seleccionar producto</MenuItem>
              {inventory?.products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name} ({product.line})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Bodega"
              variant="outlined"
              size="small"
              value={item.storage_address_id || ""}
              onChange={(e) =>
                handleItemChange(
                  index,
                  "storage_address_id",
                  parseInt(e.target.value, 10) || 0
                )
              }
              inputProps={{ "aria-label": `address-id-${index}` }}
              className={styles.itemField}
              disabled={isLoadingInventory}
            >
              <MenuItem value={0}>Seleccionar bodega</MenuItem>
              {inventory?.storageAddresses.map((address) => (
                <MenuItem key={address.id} value={address.id}>
                  {address.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Cantidad"
              type="number"
              variant="outlined"
              size="small"
              value={item.quantity || ""}
              onChange={(e) =>
                handleItemChange(index, "quantity", parseInt(e.target.value, 10) || 0)
              }
              inputProps={{ min: 1, "aria-label": `quantity-${index}` }}
              className={styles.itemField}
            />
            <IconButton
              aria-label="Eliminar item"
              onClick={() => removeItem(index)}
              disabled={items.length <= 1}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </div>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addItem}
          size="small"
          className={styles.addButton}
        >
          Agregar item
        </Button>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!canConfirm || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {isEditMode ? "Guardar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReservationModal;
